import clsx from 'clsx'
import { format, formatDistanceToNowStrict } from 'date-fns'
import { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'

import PlusIcon from '@/assets/img/ico-add.svg?react'
import SearchIcon from '@/assets/img/search.svg?react'
import Image from '@/compoments/common/Image'
import NewChatModal from '@/compoments/modals/NewChatModal'
import { getUser } from '@/features/auth/authSlice'
import { getAllChats } from '@/features/chat/chatSlice'
import { useGetAllChatsQuery } from '@/services/chats/chats'
import type { User } from '@/types'
import type { IChat } from '@/types/chats'
import { getMediaUrl } from '@/utils/helpers'

export const ChatSideBar = () => {
    const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const user = useSelector(getUser)
    const [searchTerm, setSearchTerm] = useState('')
    const chats = useSelector(getAllChats)
    const { id } = useParams()
    const { data, refetch } = useGetAllChatsQuery(
        `page=${currentPage}&limit=15&sort=updatedAt&order=desc&search=${searchTerm}`,
    )
    const hasMore = currentPage < (data?.pagination?.maxPages || 0)

    useEffect(() => {
        refetch()
    }, [refetch, currentPage])

    const search = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentPage(1)
        setSearchTerm(e.target.value)
    }

    const fetchMoreData = () => {
        if (hasMore) {
            setCurrentPage((prevPage) => prevPage + 1)
        }
    }

    const findSender = (id: number, users: User[]) => {
        if (!id || !users.length) return null
        return users.find((user) => user.id === id)
    }

    return (
        <>
            <aside className="chat-sidebar">
                <div className="chat-sidebar--header">
                    <div className="form-group input--sm input--icon-left">
                        <div className="input-wrapper">
                            <span className="ico text-color-1">
                                <SearchIcon />
                            </span>
                            <input
                                className="input input--solid full-radius w-full"
                                type="text"
                                placeholder="Search chats..."
                                onChange={search}
                            />
                        </div>
                    </div>
                </div>
                <div className="chat-sidebar--body h-full">
                    <ul
                        className="list-chats"
                        id="scrollableList"
                        style={{
                            overflow: 'auto',
                            display: 'flex',
                        }}
                    >
                        <InfiniteScroll
                            dataLength={chats.length}
                            next={fetchMoreData}
                            hasMore={hasMore}
                            scrollableTarget="scrollableList"
                            loader={<></>}
                        >
                            {chats?.map((chat: IChat) => {
                                const recipient = chat?.users?.find(
                                    (u: User) => u.id !== user?.id,
                                )

                                return (
                                    <li
                                        key={chat.id}
                                        className={clsx('chat-item', {
                                            '--active-chat':
                                                id === String(chat.id),
                                        })}
                                    >
                                        <Link
                                            to={`/chat/${chat.id}`}
                                            className="chat-select"
                                        >
                                            <div className="ico-avatar ico-avatar-group btn btn--sm btn--square">
                                                {chat.isGroup &&
                                                    chat?.users?.map(
                                                        (user, index) => (
                                                            <div
                                                                key={user.id}
                                                                className={clsx(
                                                                    'ico-avatar btn btn--sm btn--square',
                                                                    {
                                                                        '--pos-top':
                                                                            index ===
                                                                            0,
                                                                        '--pos-left':
                                                                            index ===
                                                                            1,
                                                                        '--pos-right':
                                                                            index ===
                                                                            2,
                                                                    },
                                                                )}
                                                            >
                                                                <Image
                                                                    src={getMediaUrl(
                                                                        user.photo,
                                                                    )}
                                                                    alt={
                                                                        user.fullName
                                                                    }
                                                                />
                                                            </div>
                                                        ),
                                                    )}
                                                {!chat.isGroup && (
                                                    <div className="ico-avatar btn btn--sm btn--square">
                                                        <Image
                                                            src={getMediaUrl(
                                                                recipient?.photo,
                                                            )}
                                                            alt={
                                                                recipient?.fullName
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="chat-select-user-info">
                                                <div className="user-info-row">
                                                    <p className="user-info-name">
                                                        {chat.name ||
                                                            recipient?.fullName}
                                                    </p>
                                                    <p className="user-info-time">
                                                        {formatDistanceToNowStrict(
                                                            new Date(
                                                                chat
                                                                    ?.lastMessage
                                                                    ?.createdAt ??
                                                                    chat?.updatedAt ??
                                                                    Date.now(),
                                                            ),
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="user-info-row">
                                                    {chat.isGroup && (
                                                        <span className="user-info-last-name">
                                                            {
                                                                findSender(
                                                                    chat
                                                                        ?.lastMessage
                                                                        ?.senderId,
                                                                    chat?.users,
                                                                )?.fullName
                                                            }
                                                        </span>
                                                    )}
                                                    <p className="user-info-last-msg">
                                                        {
                                                            chat?.lastMessage
                                                                ?.content
                                                        }
                                                    </p>
                                                    {chat?.unreadMessagesCount >
                                                    0 ? (
                                                        <span className="unread-count">
                                                            1
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            })}
                        </InfiniteScroll>
                    </ul>
                    <button
                        onClick={() => setIsNewChatModalOpen(true)}
                        className="btn btn--square btn--xxl btn--primary-gradient full-radius new-chat-btn"
                    >
                        <span className="ico">
                            <PlusIcon />
                        </span>
                    </button>
                </div>
            </aside>
            {isNewChatModalOpen && (
                <NewChatModal
                    isOpen={isNewChatModalOpen}
                    onClose={() => setIsNewChatModalOpen(false)}
                />
            )}
        </>
    )
}
